import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TableChartIcon from '@mui/icons-material/TableChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AnalyzerApiUrl } from '../utilities/constants';

// ── Formatters ──────────────────────────────────────────────────────────────────
// Convert snake_case / kebab-case / lowercase identifiers into Title Case
// (e.g. "mark_info" → "Mark Info", "struct_tree_root" → "Struct Tree Root").
function formatCheckName(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

// ── Severity badge ──────────────────────────────────────────────────────────────
const SEVERITY_COLORS = {
  critical: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
  major:    { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
  minor:    { bg: '#fefce8', text: '#ca8a04', border: '#fde68a' },
  info:     { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
};

function SeverityChip({ severity }) {
  const colors = SEVERITY_COLORS[severity] || SEVERITY_COLORS.info;
  return (
    <Chip
      label={severity}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'capitalize',
      }}
    />
  );
}

function PassChip({ passed }) {
  return passed ? (
    <Chip
      icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#16a34a !important' }} />}
      label="Pass"
      size="small"
      sx={{ backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 700 }}
    />
  ) : (
    <Chip
      icon={<CancelIcon sx={{ fontSize: '14px !important', color: '#dc2626 !important' }} />}
      label="Fail"
      size="small"
      sx={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontWeight: 700 }}
    />
  );
}

function ComplianceBadge({ level }) {
  const pass = level === 'pass';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 2,
        py: 0.75,
        borderRadius: 2,
        backgroundColor: pass ? '#f0fdf4' : '#fef2f2',
        border: `2px solid ${pass ? '#4ade80' : '#f87171'}`,
        fontWeight: 700,
        fontSize: '0.875rem',
        color: pass ? '#15803d' : '#b91c1c',
      }}
    >
      {pass ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <CancelIcon sx={{ fontSize: 18 }} />}
      {pass ? 'COMPLIANT' : 'NON-COMPLIANT'}
    </Box>
  );
}

// ── Checks table (shared by pre/post) ───────────────────────────────────────────
function ChecksTable({ checks }) {
  if (!checks?.length) return <Typography variant="body2" sx={{ color: '#94a3b8' }}>No checks found.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
      <Table size="small" aria-label="Accessibility checks">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
            <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>Check</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>Result</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>Severity</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>Message</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checks.map((check, i) => (
            <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#5F5BCD' }}>
                  {formatCheckName(check.name)}
                </Typography>
              </TableCell>
              <TableCell><PassChip passed={check.passed} /></TableCell>
              <TableCell><SeverityChip severity={check.severity} /></TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#374151', maxWidth: 280 }}>
                  {check.message}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b', maxWidth: 220 }}>
                  {check.details ?? '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Page audits table ───────────────────────────────────────────────────────────
function PageAuditsTable({ audits }) {
  if (!audits?.length) return <Typography variant="body2" sx={{ color: '#94a3b8' }}>No page audit data found.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
      <Table size="small" aria-label="Page audits">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
            {['Page', 'Text Layer', 'Text Blocks', 'Images', 'Image Count', 'Marked Content', 'Elements Resolved', 'Elements Failed', 'Invisible Text', 'Stream Marked'].map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {audits.map((audit, i) => (
            <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
              <TableCell><Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Pg {audit.page + 1}</Typography></TableCell>
              <TableCell>{audit.has_text_layer ? <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} /> : <CancelIcon sx={{ fontSize: 16, color: '#dc2626' }} />}</TableCell>
              <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{audit.text_blocks ?? '—'}</Typography></TableCell>
              <TableCell>{audit.has_images ? <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} /> : <CancelIcon sx={{ fontSize: 16, color: '#dc2626' }} />}</TableCell>
              <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{audit.image_count ?? '—'}</Typography></TableCell>
              <TableCell>{audit.existing_marked_content ? <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} /> : <CancelIcon sx={{ fontSize: 16, color: '#dc2626' }} />}</TableCell>
              <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: 600 }}>{audit.elements_resolved ?? '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem', color: audit.elements_failed < 0 ? '#64748b' : '#dc2626' }}>{audit.elements_failed ?? '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{audit.invisible_text_inserted ?? '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{audit.content_stream_marked ?? '—'}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Summary panel ─────────────────────────────────────────────────────────────
function SummaryPanel({ summary }) {
  if (!summary) return null;
  const stats = [
    { label: 'Pages Processed', value: summary.pages_processed ?? '—' },
    { label: 'Total Elements Tagged', value: summary.total_elements_tagged ?? '—' },
    { label: 'Figures with Alt Text', value: summary.total_figures_with_alt ?? '—' },
    { label: 'Figures Missing Alt Text', value: summary.total_figures_without_alt ?? '—' },
    { label: 'Invisible Text Overlays', value: summary.invisible_text_overlays_added ?? '—' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {stats.map(({ label, value }) => (
          <Paper
            key={label}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              minWidth: 140,
              flex: '1 1 140px',
              textAlign: 'center',
              borderColor: '#e2e8f0',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#5F5BCD' }}>{value}</Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>{label}</Typography>
          </Paper>
        ))}
      </Box>

      {summary.warnings?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: 16 }} /> Warnings ({summary.warnings.length})
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: '#fde68a', backgroundColor: '#fffbeb', maxHeight: 160, overflowY: 'auto' }}>
            {summary.warnings.map((w, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', color: '#92400e', fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.6 }}>
                {w}
              </Typography>
            ))}
          </Paper>
        </Box>
      )}

      {summary.errors?.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b', mb: 1 }}>
            Errors ({summary.errors.length})
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: '#fecaca', backgroundColor: '#fef2f2', maxHeight: 120, overflowY: 'auto' }}>
            {summary.errors.map((e, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', color: '#991b1b', fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.6 }}>
                {e}
              </Typography>
            ))}
          </Paper>
        </Box>
      )}
    </Box>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────
export default function ReportModal({ open, onClose, job, idToken }) {
  const [tab, setTab] = useState(0);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingJson, setDownloadingJson] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ── Fetch report JSON ────────────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    if (!job?.requestId || !idToken) return;
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      // Get the presigned report URL from the status endpoint
      const resp = await fetch(`${AnalyzerApiUrl}/status/${job.requestId}`, {
        headers: { Authorization: idToken },
      });
      if (!resp.ok) throw new Error(`Status endpoint returned ${resp.status}`);
      const data = await resp.json();
      const reportUrl = data.report_url || job.reportUrl;
      if (!reportUrl) throw new Error('No report URL available for this job.');

      // Fetch the JSON from S3 presigned URL
      const reportResp = await fetch(reportUrl);
      if (!reportResp.ok) throw new Error(`Failed to fetch report JSON (${reportResp.status})`);
      const json = await reportResp.json();
      setReportData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [job?.requestId, idToken, job?.reportUrl]);

  useEffect(() => {
    if (open) {
      setTab(0);
      fetchReport();
    } else {
      // Reset on close
      setReportData(null);
      setError(null);
    }
  }, [open, fetchReport]);

  // ── Download JSON ────────────────────────────────────────────────────────────
  const handleDownloadJson = async () => {
    if (!reportData) return;
    setDownloadingJson(true);
    try {
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job.fileName.replace(/\.pdf$/i, '')}_accessibility_report.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingJson(false);
    }
  };

  // ── Download PDF Report ──────────────────────────────────────────────────────
  const handleDownloadPdf = async () => {
    if (!reportData) return;
    setDownloadingPdf(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 14;

      // ── Header ──────────────────────────────────────────────────────────────
      doc.setFillColor(23, 19, 75); // #17134B
      doc.rect(0, 0, pageW, 22, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('PDF Accessibility Remediation Report', margin, 14);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`File: ${job.fileName}`, margin, 19.5);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageW - margin, 19.5, { align: 'right' });

      let y = 30;

      // ── Compliance summary ───────────────────────────────────────────────────
      const preLevel = reportData.pre_remediation?.compliance_level ?? 'unknown';
      const postLevel = reportData.post_remediation?.compliance_level ?? 'unknown';

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 14, 46);
      doc.text('Compliance Overview', margin, y);
      y += 5;

      const compRows = [
        ['Pre-Remediation', preLevel.toUpperCase(), preLevel === 'pass' ? '✓ Compliant' : '✗ Non-Compliant'],
        ['Post-Remediation', postLevel.toUpperCase(), postLevel === 'pass' ? '✓ Compliant' : '✗ Non-Compliant'],
      ];
      autoTable(doc, {
        startY: y,
        head: [['Stage', 'Level', 'Status']],
        body: compRows,
        theme: 'grid',
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [95, 91, 205], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 2: { fontStyle: 'bold' } },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 2) {
            data.cell.styles.textColor = data.cell.raw.startsWith('✓') ? [22, 163, 74] : [220, 38, 38];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;

      // ── Pre-Remediation Checks ───────────────────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 14, 46);
      doc.text('Pre-Remediation Checks', margin, y);
      y += 2;

      const preRows = (reportData.pre_remediation?.checks ?? []).map((c) => [
        formatCheckName(c.name),
        c.passed ? 'PASS' : 'FAIL',
        c.severity?.toUpperCase() ?? '',
        c.message,
        c.details ?? '',
      ]);
      autoTable(doc, {
        startY: y + 2,
        head: [['Check', 'Result', 'Severity', 'Message', 'Details']],
        body: preRows,
        theme: 'striped',
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [95, 91, 205], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 36, fontStyle: 'bold' }, 1: { cellWidth: 16 }, 2: { cellWidth: 20 }, 3: { cellWidth: 90 }, 4: { cellWidth: 'auto' } },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            data.cell.styles.textColor = data.cell.raw === 'PASS' ? [22, 163, 74] : [220, 38, 38];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;

      // ── Post-Remediation Checks ──────────────────────────────────────────────
      if (y > doc.internal.pageSize.getHeight() - 50) { doc.addPage(); y = 18; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 14, 46);
      doc.text('Post-Remediation Checks', margin, y);
      y += 2;

      const postRows = (reportData.post_remediation?.checks ?? []).map((c) => [
        formatCheckName(c.name),
        c.passed ? 'PASS' : 'FAIL',
        c.severity?.toUpperCase() ?? '',
        c.message,
        c.details ?? '',
      ]);
      autoTable(doc, {
        startY: y + 2,
        head: [['Check', 'Result', 'Severity', 'Message', 'Details']],
        body: postRows,
        theme: 'striped',
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [22, 163, 74], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 36, fontStyle: 'bold' }, 1: { cellWidth: 16 }, 2: { cellWidth: 20 }, 3: { cellWidth: 90 }, 4: { cellWidth: 'auto' } },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            data.cell.styles.textColor = data.cell.raw === 'PASS' ? [22, 163, 74] : [220, 38, 38];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;

      // ── Page Audits ──────────────────────────────────────────────────────────
      if (reportData.page_audits?.length) {
        if (y > doc.internal.pageSize.getHeight() - 50) { doc.addPage(); y = 18; }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(15, 14, 46);
        doc.text('Page Audits', margin, y);
        y += 2;

        const pageRows = reportData.page_audits.map((a) => [
          `Page ${a.page + 1}`,
          a.has_text_layer ? 'Yes' : 'No',
          a.text_blocks ?? '—',
          a.has_images ? 'Yes' : 'No',
          a.image_count ?? '—',
          a.existing_marked_content ? 'Yes' : 'No',
          a.elements_resolved ?? '—',
          a.elements_failed ?? '—',
          a.invisible_text_inserted ?? '—',
          a.content_stream_marked ?? '—',
        ]);
        autoTable(doc, {
          startY: y + 2,
          head: [['Page', 'Text Layer', 'Text Blocks', 'Has Images', 'Image Count', 'Marked Content', 'Resolved', 'Failed', 'Invisible Text', 'Stream Marked']],
          body: pageRows,
          theme: 'striped',
          margin: { left: margin, right: margin },
          headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
          bodyStyles: { fontSize: 7.5 },
        });
        y = doc.lastAutoTable.finalY + 8;
      }

      // ── Summary stats ────────────────────────────────────────────────────────
      if (reportData.summary) {
        if (y > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 18; }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(15, 14, 46);
        doc.text('Summary', margin, y);
        y += 2;

        const s = reportData.summary;
        const summaryRows = [
          ['Pages Processed', String(s.pages_processed ?? '—')],
          ['Total Elements Tagged', String(s.total_elements_tagged ?? '—')],
          ['Figures with Alt Text', String(s.total_figures_with_alt ?? '—')],
          ['Figures without Alt Text', String(s.total_figures_without_alt ?? '—')],
          ['Invisible Text Overlays Added', String(s.invisible_text_overlays_added ?? '—')],
          ['Warnings', String(s.warnings?.length ?? 0)],
          ['Errors', String(s.errors?.length ?? 0)],
        ];
        autoTable(doc, {
          startY: y + 2,
          head: [['Metric', 'Value']],
          body: summaryRows,
          theme: 'grid',
          margin: { left: margin, right: margin },
          tableWidth: 100,
          headStyles: { fillColor: [95, 91, 205], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          columnStyles: { 1: { fontStyle: 'bold', halign: 'right' } },
        });
      }

      // ── Footer on all pages ──────────────────────────────────────────────────
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        doc.text(`Page ${i} of ${pageCount} — PDF Accessibility Remediation Analyzer`, pageW / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
      }

      doc.save(`${job.fileName.replace(/\.pdf$/i, '')}_accessibility_report.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const TABS = [
    { label: 'Pre-Remediation', icon: <AssessmentIcon sx={{ fontSize: 16 }} /> },
    { label: 'Post-Remediation', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    { label: 'Page Audits', icon: <TableChartIcon sx={{ fontSize: 16 }} /> },
    { label: 'Summary', icon: <SummarizeIcon sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="report-modal-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: '#ffffff',
          maxHeight: '90vh',
        },
      }}
    >
      {/* ── Title bar ─────────────────────────────────────────────────────── */}
      <DialogTitle
        id="report-modal-title"
        sx={{
          background: 'linear-gradient(135deg, #17134B 0%, #2d2870 100%)',
          color: '#fff',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
            Accessibility Report
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.65)',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 600,
            }}
            title={job?.fileName}
          >
            {job?.fileName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close report" sx={{ color: 'rgba(255,255,255,0.7)', mt: -0.5, '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
            <CircularProgress sx={{ color: '#5F5BCD' }} size={44} aria-label="Loading report" />
            <Typography variant="body2" sx={{ color: '#64748b' }}>Fetching accessibility report…</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          </Box>
        ) : reportData ? (
          <>
            {/* Compliance summary strip */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Before</Typography>
                <ComplianceBadge level={reportData.pre_remediation?.compliance_level} />
              </Box>
              <Box sx={{ fontSize: 20, color: '#64748b' }} aria-hidden="true">→</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>After</Typography>
                <ComplianceBadge level={reportData.post_remediation?.compliance_level} />
              </Box>
            </Box>

            {/* Tab navigation */}
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              aria-label="Report sections"
              sx={{
                px: 2,
                borderBottom: '1px solid #e2e8f0',
                '& .MuiTabs-indicator': { backgroundColor: '#5F5BCD', height: 3 },
                minHeight: 44,
              }}
            >
              {TABS.map((t, i) => (
                <Tab
                  key={i}
                  label={t.label}
                  icon={t.icon}
                  iconPosition="start"
                  id={`report-tab-${i}`}
                  aria-controls={`report-tabpanel-${i}`}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    minHeight: 44,
                    color: '#475569',
                    '&.Mui-selected': { color: '#5F5BCD' },
                    gap: 0.5,
                  }}
                />
              ))}
            </Tabs>

            {/* Tab panels */}
            <Box
              sx={{ p: 2.5, overflowY: 'auto', flexGrow: 1 }}
              role="tabpanel"
              id={`report-tabpanel-${tab}`}
              aria-labelledby={`report-tab-${tab}`}
              tabIndex={0}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={tab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                >
                  {tab === 0 && <ChecksTable checks={reportData.pre_remediation?.checks} />}
                  {tab === 1 && <ChecksTable checks={reportData.post_remediation?.checks} />}
                  {tab === 2 && <PageAuditsTable audits={reportData.page_audits} />}
                  {tab === 3 && <SummaryPanel summary={reportData.summary} />}
                </motion.div>
              </AnimatePresence>
            </Box>
          </>
        ) : null}
      </DialogContent>

      {/* ── Actions ──────────────────────────────────────────────────────────── */}
      {reportData && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5, gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title="Download raw JSON report">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={downloadingJson ? <CircularProgress size={12} /> : <DataObjectIcon />}
                  onClick={handleDownloadJson}
                  disabled={downloadingJson || downloadingPdf}
                  sx={{ borderColor: '#5F5BCD', color: '#5F5BCD', '&:hover': { borderColor: '#4e4ab5', backgroundColor: 'rgba(95,91,205,0.06)' } }}
                >
                  Download JSON
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Download formatted PDF report with table view">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={downloadingPdf ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <PictureAsPdfIcon />}
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || downloadingJson}
                  sx={{ backgroundColor: '#5F5BCD', '&:hover': { backgroundColor: '#4e4ab5' } }}
                >
                  {downloadingPdf ? 'Generating…' : 'Download PDF Report'}
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
